import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchSkyleadTimeline,
  fetchSkyleadLeadsByTag,
  fetchSkyleadOverview,
  fetchBdPorFuera,
} from "@/lib/api";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, Mail, Tag as TagIcon, Sparkles, ExternalLink, User } from "lucide-react";

const FORME_CLIENT_ID = "46fffad1-0e87-42bc-8bdc-7132b8ca3502";
const PRIORITY_TAGS_ORDER = [
  "Interesado", "Coordinando reunión", "Reunión OK",
  "Contactar más adelante", "Por Fuera", "En proceso",
];

const fmt = (n) => Number(n || 0).toLocaleString("es-AR");
const pct = (n) => `${Number(n || 0).toFixed(1)}%`;
const fmtDate = (s) => {
  if (!s) return "";
  const d = new Date(s);
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "short" });
};

export default function ReporteOutreachSection({ clientId, hasOutreach, hasPorFuera }) {
  if (!hasOutreach) return null;

  return (
    <div className="space-y-5" data-testid="reporte-outreach">
      <OutreachKpiAndCharts clientId={clientId} />
      {(clientId === FORME_CLIENT_ID || hasPorFuera) && <PorFueraSummary clientId={clientId} />}
    </div>
  );
}

/* ── KPIs + 2 charts (timeline & emails) ── */
function OutreachKpiAndCharts({ clientId }) {
  const [accountId, setAccountId] = useState("all");

  // First pull all accounts (overview without filter) so we can populate the dropdown.
  const profilesQ = useQuery({
    queryKey: ["sk-overview-profiles", clientId],
    queryFn: () => fetchSkyleadOverview(clientId),
    staleTime: 5 * 60_000,
  });
  const profiles = profilesQ.data?.profiles || [];

  const filterId = accountId === "all" ? null : accountId;
  const overviewQ = useQuery({
    queryKey: ["sk-overview", clientId, filterId],
    queryFn: () => fetchSkyleadOverview(clientId, filterId),
    staleTime: 60_000,
    enabled: !profilesQ.isLoading,
  });
  const tlQ = useQuery({
    queryKey: ["sk-timeline", clientId, 30, filterId],
    queryFn: () => fetchSkyleadTimeline(clientId, 30, filterId),
    staleTime: 60_000,
    enabled: !profilesQ.isLoading,
  });
  const totals = overviewQ.data?.totals || {};
  const tl = tlQ.data || { invites: [], emails: [], totals: {} };
  const selectedLabel = accountId === "all"
    ? "Todos los SDRs"
    : (profiles.find((p) => String(p.account_id) === String(accountId))?.name || `Seat ${accountId}`);

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="reporte-outreach-charts">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5"><Sparkles className="h-3 w-3" /> Outreach</p>
          <h3 className="font-display text-sm font-semibold text-slate-900">
            Volumen · últimos 30 días
            <span className="ml-2 text-[11px] font-medium text-slate-500">· {selectedLabel}</span>
          </h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {profiles.length > 1 && (
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger className="h-8 text-xs w-56" data-testid="sdr-account-selector">
                <User className="h-3 w-3 mr-1.5 text-slate-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="sdr-option-all">Todos los SDRs ({profiles.length})</SelectItem>
                {profiles.map((p) => (
                  <SelectItem key={p.account_id} value={String(p.account_id)} data-testid={`sdr-option-${p.account_id}`}>
                    {p.name || `Seat #${p.account_id}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <div className="flex gap-2 flex-wrap text-[11px]">
            <MiniStat label="Invites" value={fmt(totals.connections_requested)} accent="sky" />
            <MiniStat label="Aceptación" value={pct(totals.acceptance_rate)} accent="emerald" />
            <MiniStat label="Respuestas" value={fmt(totals.connection_replies)} accent="indigo" />
            <MiniStat label="Emails" value={fmt(totals.emails_sent)} accent="amber" />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartFrame
          title="Invitaciones · aceptaciones · respuestas"
          totalsText={`Sent ${fmt(tl.totals.invitations_sent)} · Accepted ${fmt(tl.totals.invitations_accepted)} · Replies ${fmt(tl.totals.message_replies)}`}
          loading={tlQ.isLoading}
        >
          <LineChart data={tl.invites || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="date" tickFormatter={fmtDate} fontSize={11} stroke="#64748b" />
            <YAxis fontSize={11} stroke="#64748b" />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="sent" name="Enviadas" stroke="#0EA5E9" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="accepted" name="Aceptadas" stroke="#10B981" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="replied" name="Respuestas" stroke="#8B5CF6" strokeWidth={2} dot={false} />
          </LineChart>
        </ChartFrame>
        <ChartFrame
          title="Emails · descubiertos · enviados · abiertos"
          totalsText={`Verificados ${fmt(tl.totals.emails_verified)} · Sent ${fmt(tl.totals.emails_sent)} · Opened ${fmt(tl.totals.emails_opened)}`}
          loading={tlQ.isLoading}
        >
          <LineChart data={tl.emails || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="date" tickFormatter={fmtDate} fontSize={11} stroke="#64748b" />
            <YAxis fontSize={11} stroke="#64748b" />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="verified" name="Descubiertos" stroke="#F59E0B" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="sent" name="Enviados" stroke="#0EA5E9" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="opened" name="Abiertos" stroke="#10B981" strokeWidth={2} dot={false} />
          </LineChart>
        </ChartFrame>
      </div>
    </section>
  );
}

/* ── Tags summary ── */
function TagsSummary({ clientId }) {
  const q = useQuery({
    queryKey: ["sk-leads-by-tag", clientId],
    queryFn: () => fetchSkyleadLeadsByTag(clientId, 30),
    staleTime: 5 * 60_000,
  });
  const sorted = useMemo(() => {
    const dist = q.data?.tag_distribution || [];
    // Priority tags first (if present), then everything else by count desc.
    const priorityFound = PRIORITY_TAGS_ORDER
      .map((tag) => dist.find((d) => d.tag === tag))
      .filter(Boolean);
    const rest = dist.filter((d) => !PRIORITY_TAGS_ORDER.includes(d.tag));
    return [...priorityFound, ...rest];
  }, [q.data]);
  const total = useMemo(() => (q.data?.leads || []).length, [q.data]);

  const tagTone = (tag) => {
    const t = tag.toLowerCase();
    if (t.includes("interesa") && !t.includes("no")) return "emerald";
    if (t.includes("coordinando") || t.includes("reunión ok") || t.includes("reunion ok")) return "indigo";
    if (t.includes("contactar")) return "amber";
    if (t.includes("por fuera")) return "violet";
    if (t.includes("en proceso") || t.includes("conversación") || t.includes("conversacion")) return "sky";
    if (t.includes("no interes") || t.includes("no avanza")) return "rose";
    return "slate";
  };

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="reporte-outreach-tags">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5"><TagIcon className="h-3 w-3" /> Conversaciones outbound</p>
          <h3 className="font-display text-sm font-semibold text-slate-900">Total por tag</h3>
        </div>
        <div className="text-[11px] text-slate-500">{q.isLoading ? "Escaneando…" : `${total} leads etiquetados`}</div>
      </div>
      {q.isLoading ? (
        <div className="h-24 skeleton rounded-md" />
      ) : sorted.length === 0 ? (
        <div className="text-[11px] text-slate-400 italic">Sin leads etiquetados encontrados.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {sorted.slice(0, 12).map((t) => (
            <TagBlock key={t.tag} label={t.tag} count={t.count} total={total} tone={tagTone(t.tag)} />
          ))}
        </div>
      )}
    </section>
  );
}

function TagBlock({ label, count, total, tone }) {
  const tonal = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    violet: "bg-violet-50 border-violet-200 text-violet-800",
    sky: "bg-sky-50 border-sky-200 text-sky-800",
    rose: "bg-rose-50 border-rose-200 text-rose-800",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
  }[tone] || "bg-slate-50 border-slate-200 text-slate-700";
  const share = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className={`border rounded-md px-3 py-2 ${tonal}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold truncate" title={label}>{label}</div>
      <div className="flex items-baseline gap-1.5">
        <div className="text-xl font-semibold tabular-nums">{count}</div>
        <div className="text-[10px] opacity-75">{share}%</div>
      </div>
    </div>
  );
}

/* ── Por Fuera summary (For Me only) ── */
function PorFueraSummary({ clientId }) {
  const q = useQuery({
    queryKey: ["bd-porfuera", clientId],
    queryFn: () => fetchBdPorFuera(clientId),
    staleTime: 60_000,
  });
  if (q.isLoading) return <div className="h-32 rounded-md skeleton" />;
  const d = q.data || {};

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="reporte-porfuera">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5"><Mail className="h-3 w-3" /> Manual</p>
          <h3 className="font-display text-sm font-semibold text-slate-900">Por Fuera · resumen general</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">{d.count} contactos · referidos, correos directos y WhatsApp</p>
        </div>
        {d.sheet_url && (
          <a href={d.sheet_url} target="_blank" rel="noreferrer" className="text-[11px] text-indigo-600 hover:underline inline-flex items-center gap-1">
            Abrir planilla <ExternalLink className="h-2.5 w-2.5" />
          </a>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Block title="Por estado" items={d.by_estado || []} />
        <Block title="Por canal" items={d.by_canal || []} />
      </div>
    </section>
  );
}

function Block({ title, items }) {
  const max = Math.max(1, ...(items || []).map((i) => i.count));
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">{title}</div>
      {(items || []).slice(0, 6).map((it) => (
        <div key={it.label} className="mb-1.5 last:mb-0">
          <div className="flex items-center justify-between text-[11px] text-slate-700 mb-0.5">
            <span className="truncate" title={it.label}>{it.label}</span>
            <span className="font-medium tabular-nums">{it.count}</span>
          </div>
          <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-indigo-500" style={{ width: `${(it.count / max) * 100}%` }} />
          </div>
        </div>
      ))}
      {(items || []).length === 0 && <div className="text-[11px] text-slate-400 italic">Sin datos.</div>}
    </div>
  );
}

/* ── small helpers ── */
function MiniStat({ label, value, accent }) {
  const tone = {
    sky: "text-sky-700 bg-sky-50 border-sky-200",
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    indigo: "text-indigo-700 bg-indigo-50 border-indigo-200",
    amber: "text-amber-700 bg-amber-50 border-amber-200",
  }[accent] || "text-slate-700 bg-slate-50 border-slate-200";
  return (
    <div className={`border rounded-md px-2.5 py-1.5 ${tone}`}>
      <div className="text-[9px] uppercase tracking-wider font-semibold opacity-75">{label}</div>
      <div className="text-sm font-semibold tabular-nums leading-tight">{value}</div>
    </div>
  );
}
function ChartFrame({ title, totalsText, loading, children }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
      <div className="mb-2">
        <h4 className="font-display text-xs font-semibold text-slate-900">{title}</h4>
        <p className="text-[10px] text-slate-500">{totalsText}</p>
      </div>
      <div className="h-52">
        {loading ? <div className="h-full skeleton rounded-md" /> : <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>}
      </div>
    </div>
  );
}
